import React, { PropsWithChildren, ReactNode, useEffect, useRef, useState } from 'react';
import { StyleLabels } from '../../lib/styles';
import { TAppElkImageStyle } from 'TProtocol/prototypes/events/messages';
import { ButtonContainer } from './GenerateButton';
import styled from 'styled-components';
import { useBackgroundContext } from '../../contexts/BackgroundContext';
import { calculateButtonColors } from '../../lib/buttonColors';
import ActionButton from './ActionButton';

interface Props {
  buttonText: ReactNode;
  disabled: boolean;
  style: TAppElkImageStyle;
  onStyleChange: (style: TAppElkImageStyle) => void;
}

const MENU_ITEMS = (Object.keys(StyleLabels) as unknown as (keyof typeof StyleLabels)[]).map((id) => {
  return {
    id,
    label: StyleLabels[id]
  };
}).sort((a, b) => {
  if (+a.id === TAppElkImageStyle.DEFAULT) {
    return -1;
  }
  if (+b.id === TAppElkImageStyle.DEFAULT) {
    return 1;
  }
  return (a.label < b.label) ? -1 : 1;
});

const ImageStyleDropdownButton = ({
                                    buttonText,
                                    disabled,
                                    style,
                                    onStyleChange
                                  }: PropsWithChildren<Props>) => {
  const backgroundContext = useBackgroundContext();

  const [showMenu, setShowMenu] = useState(false);
  const menuDivRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClick = (event: MouseEvent) => {
      if (menuDivRef.current && !menuDivRef.current.contains(event.target as Node)) {
        setShowMenu(false);
      }
    };

    document.addEventListener('click', handleClick);

    return () => {
      document.removeEventListener('click', handleClick);
    };
  }, []);

  const { borderColor, textColor, bgColor } = calculateButtonColors({
    color: backgroundContext.colors.highlightText
  });

  const onDropdownClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setShowMenu(true);
  };

  const handleMenuClick = (id: TAppElkImageStyle) => {
    setShowMenu(false);
    onStyleChange(id);
  };

  return <DropdownButtonContainer>
    <ButtonContainer>
      <ButtonLabel>Image Filter/Style</ButtonLabel>
      <ActionButton disabled={disabled} onClick={onDropdownClick} lessPadding={true} maxHeight={30}
                    noMarginTop={true}>
        <span style={{ flexGrow: 1, padding: '0 10px' }}>
          <TextWrapper>{buttonText}</TextWrapper>
        </span>
        <DropdownIcon $color={textColor}/>
      </ActionButton>
    </ButtonContainer>
    <DropdownMenuAnchor>
      {showMenu && <DropdownMenu ref={menuDivRef}>
        {
          MENU_ITEMS.map((menuItem) => <MenuItem
            key={`${menuItem.id}`}
            onClick={() => handleMenuClick(menuItem.id)}
            $selected={menuItem.id === style}
          >
            {menuItem.label}
          </MenuItem>)
        }
      </DropdownMenu>}
    </DropdownMenuAnchor>
  </DropdownButtonContainer>;
};


const TextWrapper = styled.div`
  justify-content: center;
  align-content: center;
  align-items: center;
  font-size: 16px;
  font-style: normal;
  font-weight: 300;
  line-height: 160%; /* 28.8px */
  margin: 10px 10px;
`;

const DropdownIcon = styled.span<{ $color: string }>`
  display: inline-block;
  width: 0;
  height: 0;
  margin: 0 5px;
  content: "";
  border-color: ${({ $color }) => $color};
  border-top: .3em solid;
  border-right: .3em solid transparent;
  border-bottom: 0;
  border-left: .3em solid transparent;
`;

const DropdownMenuAnchor = styled.div`
  height: 0;
  position: relative;
  z-index: 200;
`;

const DropdownMenu = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  background-color: #c9c9c9;
  color: black;
  border-radius: 6px;
  box-shadow: 0 0 24px 0 rgba(0, 0, 0, 0.5);
`;

const MenuItem = styled.div<{ $selected: boolean }>`
  cursor: pointer;
  padding: 6px 10px;

  ${({ $selected }) => $selected && `
    background-color: var(--shine-highlight-color);
    color: var(--shine-system-button-text-color);
  `}
  &:hover {
    background-color: var(--shine-highlight-color);
    color: var(--shine-system-button-text-color);
  }

  &:first-of-type {
    border-radius: 6px 6px 0 0;
  }

  &:last-of-type {
    border-radius: 0 0 6px 6px;
  }
`;

const ButtonLabel = styled.div`
  font-weight: 350;
  width: 100%;
  color: var(--shine-background-text-color);
`

const DropdownButtonContainer = styled.div`
  width: 100%;
  padding: 2px;
`

export default ImageStyleDropdownButton;
